import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { chaveDaCompra, descricaoDaCompra, linhaDe, podeAbater } from '@domain/finance'
import type { OffsetLink, Transaction } from '@domain/finance'
import { addDays, dayAndMonth } from '@shared/date/civil'
import { sumCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { normalizedText } from '@domain/product'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { ANTES_DO_GASTO, DEPOIS_DO_GASTO, entradasCandidatas, mesesDaJanela } from './candidatas'

/**
 * "Abater com entradas" — o arranjo de "Escolher a transação" da nota
 * (`VincularTransacao`): a sugestão em cima, o resto numa dobra fechada com
 * busca. A sugestão é a entrada com EXATAMENTE o valor que falta abater — o
 * caso da compra feita para a mãe. A tela sugere e não marca sozinha
 * (princípio 5).
 *
 * Passar do gasto: a folha diz quanto passou, e "Abater" fica desabilitado —
 * o motivo está escrito logo acima dele (decisão 311).
 */
export function FolhaAbaterComEntradas({
  gasto,
  valorDaCompraCents,
  ligadas,
  onFechar,
  onGravou,
}: {
  gasto: Transaction
  valorDaCompraCents: number
  ligadas: Transaction[]
  onFechar: () => void
  onGravou: () => void
}) {
  const { transactionRepository } = useFinanceRepositories()
  const [lidas, setLidas] = useState<Transaction[] | 'carregando' | 'falhou'>('carregando')
  // Só as ligadas que ainda abatem: a que o banco cancelou sai da conta (o
  // gatilho a desliga), e contá-la no "que falta" apagava a sugestão certa.
  const vivasLigadas = ligadas.filter((t) => podeAbater(linhaDe(t)))
  const ehLigada = (t: Transaction) => vivasLigadas.some((l) => l.id === t.id)
  const [marcadas, setMarcadas] = useState(() => new Set(vivasLigadas.map((t) => t.id)))
  const [busca, setBusca] = useState('')
  const [gravando, setGravando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  const data = gasto.source.transactionDate
  const meses = useMemo(
    () => mesesDaJanela(addDays(data, -ANTES_DO_GASTO), addDays(data, DEPOIS_DO_GASTO)),
    [data],
  )

  useEffect(() => {
    let vivo = true
    void Promise.all(meses.map((m) => transactionRepository.listByCompetence(m)))
      .then((porMes) => {
        if (vivo) setLidas(porMes.flat())
      })
      .catch((erro: unknown) => {
        console.error('FolhaAbaterComEntradas: a leitura falhou', erro)
        if (vivo) setLidas('falhou')
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, meses])

  // As ligadas entram SEMPRE, fora da janela de datas inclusive: uma entrada
  // ligada que a janela deixasse de fora continuaria marcada sem aparecer —
  // fora da conta ao vivo, e impossível de desmarcar.
  const novas =
    lidas === 'carregando' || lidas === 'falhou'
      ? []
      : entradasCandidatas(lidas, gasto).filter((t) => !ehLigada(t))
  const candidatas = [...vivasLigadas, ...novas]
  const jaAbatido = sumCents(vivasLigadas.map((t) => t.source.amountCents))
  const falta = valorDaCompraCents - jaAbatido
  const sugestoes = novas.filter((t) => falta > 0 && t.source.amountCents === falta)
  const termo = normalizedText(busca)
  // O número do rótulo é o da lista que a dobra mostra — sem as ligadas, que
  // têm grupo próprio, e sem as sugestões, que estão acima.
  const outras = novas
    .filter((t) => !sugestoes.includes(t))
    .filter((t) => termo === '' || normalizedText(t.source.description).includes(termo))

  const somaMarcada = sumCents(
    candidatas.filter((t) => marcadas.has(t.id)).map((t) => t.source.amountCents),
  )
  const excesso = somaMarcada - valorDaCompraCents

  const alternar = (id: string) => {
    setMarcadas((atual) => {
      const nova = new Set(atual)
      if (nova.has(id)) nova.delete(id)
      else nova.add(id)
      return nova
    })
  }

  const gravar = () => {
    const vinculo: OffsetLink = {
      purchaseKey: chaveDaCompra(gasto),
      transactionId: gasto.id,
      description: descricaoDaCompra(gasto),
    }
    const ligar = candidatas
      .filter((t) => marcadas.has(t.id) && t.enrichment.offsetOf === null)
      .map((t) => ({ id: t.id, offsetOf: vinculo }))
    const desligar = vivasLigadas
      .filter((t) => !marcadas.has(t.id))
      .map((t) => ({ id: t.id, offsetOf: null }))
    setGravando(true)
    setFalhou(false)
    transactionRepository
      .setOffsetLinks([...ligar, ...desligar])
      .then(onGravou)
      .catch(() => {
        setFalhou(true)
      })
      .finally(() => {
        setGravando(false)
      })
  }

  const linha = (t: Transaction) => (
    <li key={t.id}>
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={marcadas.has(t.id)}
          onChange={() => {
            alternar(t.id)
          }}
        />
        <span className="min-w-0 flex-1 truncate">
          {`${t.source.description} · ${dayAndMonth(t.source.transactionDate)}`}
        </span>
        <span className="tabular-nums">{formatBRL(t.source.amountCents)}</span>
      </label>
    </li>
  )

  // Pelo portal, no `body`: o detalhe mora num vidro com `backdrop-filter`, que
  // vira a referência de todo `fixed` lá dentro (decisão 242e) — renderizada no
  // lugar, a folha ficava no fim da página, atrás da barra de abas. Mesmo
  // arranjo de `EscolhaEmTelaCheia`.
  return createPortal(
    <FolhaDeBaixo titulo="Abater com entradas" onFechar={onFechar}>
      <div className="flex flex-col gap-3 p-1">
        <div>
          <h2 className="titulo text-lg">Abater com entradas</h2>
          <p className="mt-0.5 text-xs text-[var(--text-muted)]">
            {`${gasto.source.description} · ${formatBRL(-valorDaCompraCents)}`}
          </p>
        </div>

        {lidas === 'carregando' && (
          <p role="status" className="text-sm text-[var(--text-muted)]">Procurando entradas…</p>
        )}
        {lidas === 'falhou' && (
          <p role="alert" className="text-sm">Não foi possível carregar as entradas.</p>
        )}

        {sugestoes.length > 0 && (
          <ul role="group" aria-label="Sugestão">
            {sugestoes.map(linha)}
          </ul>
        )}
        {vivasLigadas.length > 0 && (
          <ul role="group" aria-label="Já abatem esta compra">
            {vivasLigadas.map(linha)}
          </ul>
        )}

        <details className="group border-t border-[var(--border)]">
          <summary className="alvo flex w-full cursor-pointer list-none items-center justify-between gap-2 text-sm text-[var(--text-muted)]">
            <span>{`Outras entradas (${String(outras.length)})`}</span>
            <span aria-hidden="true" className="transition-transform group-open:rotate-90">›</span>
          </summary>
          <input
            type="search"
            value={busca}
            placeholder="Buscar pela descrição"
            aria-label="Buscar entrada"
            className="mb-2 min-h-11 w-full rounded-lg border border-[var(--border)] bg-transparent px-3 text-sm"
            onChange={(e) => {
              setBusca(e.target.value)
            }}
          />
          <ul>{outras.map(linha)}</ul>
        </details>

        <p className="text-sm tabular-nums">
          {`Abatido ${formatBRL(Math.min(somaMarcada, valorDaCompraCents))} · fica com você ${formatBRL(Math.max(0, valorDaCompraCents - somaMarcada))}`}
        </p>
        {excesso > 0 && (
          <p role="alert" className="text-sm" style={{ color: 'var(--pendente)' }}>
            {`As entradas marcadas passam do gasto em ${formatBRL(excesso)}.`}
          </p>
        )}
        {falhou && (
          <p role="alert" className="text-sm text-[var(--text-muted)]">Não foi possível salvar agora.</p>
        )}

        <div className="flex flex-wrap gap-2">
          <Botao tom="primario" type="button" disabled={excesso > 0 || gravando} onClick={gravar}>
            Abater
          </Botao>
          <button type="button" onClick={onFechar} className="alvo px-3 text-sm text-[var(--text-muted)]">
            Cancelar
          </button>
        </div>
      </div>
    </FolhaDeBaixo>,
    document.body,
  )
}
